import { createSelector } from '@reduxjs/toolkit';
import {
  alertSources,
  DEFAULT_SELECTED_ALERT_SOURCES,
} from 'features/Weather/features/WeatherAlerts/config/alertSources.config';
import { getAlertStartTime } from 'features/Weather/features/WeatherAlerts/utils/alertData.utils';
import { isFemaAlert } from 'features/Weather/features/WeatherAlerts/utils/isFemaAlert.utils';
import { parseDateTime } from 'utils/dates/date.utils';

// Base selector for alert slice state.
export const selectAlertListState = (state) => state.alertList; // Access the alert slice state

export const selectAlertList = (state) => selectAlertListState(state).alertList;

export const selectListConfig = (state) => selectAlertListState(state).listConfig;

export const selectPageIndex = (state) => selectListConfig(state).pageIndex;

export const selectSortField = (state) => selectListConfig(state).sortField ?? 'newest';

export const selectShowLocalOnly = (state) => selectListConfig(state).showLocalOnly;

export const selectMaxDist = (state) => selectListConfig(state).maxDist;

export const selectSearchTerm = (state) => selectListConfig(state).searchTerm;

export const selectSelectedAlertConfig = (state) => selectAlertListState(state).selectedAlertConfig;

export const selectAlertId = (state) => selectSelectedAlertConfig(state).alertId;

export const selectMapCenter = (state) => selectSelectedAlertConfig(state).mapCenter;

export const selectMapPolygon = (state) => selectSelectedAlertConfig(state).mapPolygon;

export const selectShowCloseAlertModal = (state) => selectAlertListState(state).showCloseAlertModal;

// Memoized selectors can be added here to derive more complex state from the alert slice
/**
 * Filters alerts by text, selected sources, selected types, and local distance.
 *
 * @param {object} state - Redux state with the Alert List slice.
 * @returns {object[]} Alerts that match the committed filters.
 */
export const selectFilteredAlertList = createSelector(
  [selectAlertList, selectListConfig],
  (alertList, listConfig) => {
    const {
      searchTerm,
      showLocalOnly,
      maxDist,
      selectedSources = DEFAULT_SELECTED_ALERT_SOURCES,
      alertTypesBySource = {},
    } = listConfig;
    const normalizedSearchTerm = searchTerm?.toLowerCase().replace(/\s/g, '');
    const hasSelectedSources = selectedSources.length > 0;
    const hasAllSourcesSelected = alertSources.every(({ id }) => selectedSources.includes(id));

    return alertList.filter((alert) => {
      const searchableText = [
        alert.title,
        alert.headline,
        alert.type,
        alert.smpp_message,
        alert.message,
        alert.description,
        alert.areaDescription,
        alert.city_name,
        alert.state_name,
        alert.name,
        alert.location,
        alert.nws_office,
      ];
      const matchesSearchTerm =
        !normalizedSearchTerm ||
        searchableText.some(
          (value) =>
            typeof value === 'string' &&
            value.toLowerCase().replace(/\s/g, '').includes(normalizedSearchTerm),
        );

      if (!matchesSearchTerm) return false; // Exclude alerts that do not match the search term

      const explicitlyConfiguredSource = alertSources.find(
        ({ alertSource: configuredAlertSource }) =>
          configuredAlertSource && alert.alertSource === configuredAlertSource,
      );
      const providerSource = alertSources.find(({ providerValues = [] }) =>
        providerValues.includes(alert.provider),
      );
      const alertSource = isFemaAlert(alert)
        ? 'fema'
        : (explicitlyConfiguredSource?.id ?? providerSource?.id ?? null);
      const sourceIsSelected =
        !hasSelectedSources ||
        (alertSource ? selectedSources.includes(alertSource) : hasAllSourcesSelected);
      if (!sourceIsSelected) return false;

      const selectedTypes = alertSource ? (alertTypesBySource[alertSource] ?? []) : [];
      if (selectedTypes.length > 0) {
        let typeValue = typeof alert.description === 'string' ? alert.description : '';
        if (alertSource === 'fema') {
          const info = Array.isArray(alert.deserialize?.message?.info)
            ? alert.deserialize.message.info
            : alert.femaInfo;
          const eventCodes = Array.isArray(info)
            ? info.flatMap((item) => item?.eventCode ?? [])
            : [];
          typeValue = eventCodes.find((eventCode) => eventCode.valueName === 'SAME')?.value ?? '';
        }

        if (!selectedTypes.includes(typeValue)) return false;
      }

      if (showLocalOnly) return alert.distanceToUser <= maxDist; // Only include alerts within maxDist if showLocalOnly is true

      return true; // Include all alerts that pass the search term and type checks
    });
  },
);

/**
 * Sorts filtered alerts by CAP severity or time when available, then by provider fields.
 *
 * @param {object} state - Redux state with the Alert List slice.
 * @returns {object[]} Alerts in the selected sort order.
 */
export const selectSortedAlertList = createSelector(
  [selectFilteredAlertList, selectSortField],
  (filteredAlertList, sortField) => {
    const sortedList = [...filteredAlertList];
    const capSeverityScore = { Unknown: 0, Minor: 10, Moderate: 30, Severe: 60, Extreme: 90 };
    const getSortSeverity = (alert) => {
      const capSeverity = alert.deserialize?.message?.info?.[0]?.severity;
      return Object.hasOwn(capSeverityScore, capSeverity)
        ? capSeverityScore[capSeverity]
        : (alert.severity ?? 0);
    };

    if (['severity', 'mostSevere', 'leastSevere'].includes(sortField)) {
      const sortDirection = sortField === 'leastSevere' ? 1 : -1;
      sortedList.sort(
        (firstAlert, secondAlert) =>
          sortDirection * (getSortSeverity(firstAlert) - getSortSeverity(secondAlert)),
      );
      return sortedList;
    }

    sortedList.sort((firstAlert, secondAlert) => {
      const firstStartTime =
        parseDateTime(
          firstAlert.deserialize?.message?.info?.[0]?.effective ??
            firstAlert.deserialize?.message?.sent ??
            getAlertStartTime(firstAlert),
        )?.getTime() ?? 0;
      const secondStartTime =
        parseDateTime(
          secondAlert.deserialize?.message?.info?.[0]?.effective ??
            secondAlert.deserialize?.message?.sent ??
            getAlertStartTime(secondAlert),
        )?.getTime() ?? 0;
      return sortField === 'oldest'
        ? firstStartTime - secondStartTime
        : secondStartTime - firstStartTime;
    });

    return sortedList;
  },
);

export const selectSortedPagedAlertList = createSelector(
  [selectSortedAlertList, selectListConfig],
  (sortedAlertList, listConfig) => {
    const { pageIndex } = listConfig;
    const alertLimit = 100;
    const pageStart = alertLimit * (pageIndex - 1);
    const pageEnd = pageStart + alertLimit;

    return sortedAlertList.slice(pageStart, pageEnd);
  },
);
